import { useState, useMemo } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Calendar, 
  ListTodo, 
  Flag,
  Maximize2,
  Minimize2,
  Loader2,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

const ProjectTimeline = ({ project, onTaskClick }) => {
  const [viewMode, setViewMode] = useState(ViewMode.Week);
  const [isChecked, setIsChecked] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getProjectProgress = (proj) => {
    if (!proj?.tasks?.length) return 0;
    const completedTasks = proj.tasks.filter(t => t.status === 'Done').length;
    return Math.round((completedTasks / proj.tasks.length) * 100);
  };

  // Convert project tasks to Gantt format
  const ganttTasks = useMemo(() => {
    if (!project?.tasks?.length) return [];

    const tasks = [];
    const today = new Date();
    
    // Add project as parent
    const projectStart = project.start_date 
      ? new Date(project.start_date) 
      : new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const projectEnd = project.target_end_date 
      ? new Date(project.target_end_date) 
      : new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    tasks.push({
      id: project.id,
      name: project.project_name,
      start: projectStart,
      end: projectEnd,
      progress: getProjectProgress(project),
      type: 'project',
      hideChildren: false,
      styles: {
        backgroundColor: '#235894',
        backgroundSelectedColor: '#1E4B80',
        progressColor: '#26B8A7',
        progressSelectedColor: '#1fa393',
      }
    });

    // Add tasks
    project.tasks.forEach((task, index) => {
      const taskStart = task.due_date 
        ? new Date(new Date(task.due_date).getTime() - 7 * 24 * 60 * 60 * 1000) 
        : new Date(projectStart.getTime() + (index * 3) * 24 * 60 * 60 * 1000);
      const taskEnd = task.due_date 
        ? new Date(task.due_date) 
        : new Date(taskStart.getTime() + 7 * 24 * 60 * 60 * 1000);

      // Determine progress based on status
      let progress = 0;
      if (task.status === 'Done') progress = 100;
      else if (task.status === 'In Progress') progress = 50;

      // Determine if overdue
      const isOverdue = task.due_date && new Date(task.due_date) < today && task.status !== 'Done';

      tasks.push({
        id: task.id,
        name: task.title,
        start: taskStart,
        end: taskEnd,
        progress,
        type: 'task',
        project: project.id,
        dependencies: (task.dependencies || []).filter(depId => 
          project.tasks.some(t => t.id === depId)
        ),
        styles: {
          backgroundColor: isOverdue ? '#EF4444' : '#64748B',
          backgroundSelectedColor: isOverdue ? '#DC2626' : '#475569',
          progressColor: isOverdue ? '#FCA5A5' : '#26B8A7',
          progressSelectedColor: isOverdue ? '#F87171' : '#1fa393',
        }
      });
    });

    // Add milestones (RAID items with due dates)
    if (project.raid_items) {
      project.raid_items
        .filter(item => item.due_date && (item.type === 'Risk' || item.type === 'Issue'))
        .forEach(item => {
          const milestoneDate = new Date(item.due_date);
          tasks.push({
            id: `milestone-${item.id}`,
            name: `⚠️ ${item.type}: ${item.description.substring(0, 30)}...`,
            start: milestoneDate,
            end: milestoneDate,
            progress: item.status === 'Closed' || item.status === 'Resolved' ? 100 : 0,
            type: 'milestone',
            project: project.id,
            styles: {
              backgroundColor: item.type === 'Risk' ? '#EF4444' : '#F59E0B',
              backgroundSelectedColor: item.type === 'Risk' ? '#DC2626' : '#D97706',
            }
          });
        });
    }

    return tasks;
  }, [project]);

  const handleTaskChange = (task) => {
    // Optional: Handle task date changes
    console.log('Task changed:', task);
  };

  const handleProgressChange = (task) => {
    // Optional: Handle progress changes
    console.log('Progress changed:', task);
  };

  const handleSelect = (task, isSelected) => {
    if (isSelected && onTaskClick && task.type === 'task') {
      onTaskClick(task);
    }
  };

  if (!project) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
          <p className="text-slate-500">Loading timeline...</p>
        </CardContent>
      </Card>
    );
  }

  if (!ganttTasks.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#235894]" />
            Project Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <ListTodo className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500">No tasks with dates to display</p>
          <p className="text-sm text-slate-400 mt-1">Add tasks with due dates to see the timeline</p>
        </CardContent>
      </Card>
    );
  }

  // Gantt chart component (reusable for both inline and fullscreen)
  const GanttChart = ({ height = 300, columnWidth = null }) => (
    <Gantt
      tasks={ganttTasks}
      viewMode={viewMode}
      onDateChange={handleTaskChange}
      onProgressChange={handleProgressChange}
      onSelect={handleSelect}
      listCellWidth={isChecked ? "155px" : ""}
      columnWidth={columnWidth || (viewMode === ViewMode.Month ? 300 : viewMode === ViewMode.Week ? 200 : 60)}
      ganttHeight={height}
      barCornerRadius={4}
      barFill={60}
      handleWidth={8}
      todayColor="rgba(38, 184, 167, 0.1)"
      projectBackgroundColor="#235894"
      projectProgressColor="#26B8A7"
      projectBackgroundSelectedColor="#1E4B80"
      projectProgressSelectedColor="#1fa393"
    />
  );

  // Legend component (reusable)
  const Legend = () => (
    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded bg-[#235894]" />
        <span>Project</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded bg-[#64748B]" />
        <span>Task</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded bg-[#EF4444]" />
        <span>Overdue</span>
      </div>
      <div className="flex items-center gap-1">
        <Flag className="w-3 h-3 text-amber-500" />
        <span>Milestone</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded bg-[#26B8A7]" />
        <span>Progress</span>
      </div>
    </div>
  );

  return (
    <>
      <Card data-testid="project-timeline">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#235894]" />
              Project Timeline
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={viewMode} onValueChange={(v) => setViewMode(v)}>
                <SelectTrigger className="w-28" data-testid="timeline-view-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ViewMode.Day}>Day</SelectItem>
                  <SelectItem value={ViewMode.Week}>Week</SelectItem>
                  <SelectItem value={ViewMode.Month}>Month</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(true)}
                data-testid="expand-timeline-btn"
              >
                <Maximize2 className="w-4 h-4 mr-1" />
                Expand
              </Button>
            </div>
          </div>
          
          {/* Legend */}
          <div className="mt-3">
            <Legend />
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <div style={{ minWidth: '600px' }}>
            <GanttChart height={300} />
          </div>
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b bg-slate-50">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#235894]" />
                {project?.project_name} - Timeline
              </DialogTitle>
              <div className="flex items-center gap-3">
                <Legend />
                <Select value={viewMode} onValueChange={(v) => setViewMode(v)}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ViewMode.Day}>Day</SelectItem>
                    <SelectItem value={ViewMode.Week}>Week</SelectItem>
                    <SelectItem value={ViewMode.Month}>Month</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-4" style={{ height: 'calc(90vh - 80px)' }}>
            <GanttChart 
              height={Math.max(500, ganttTasks.length * 50)} 
              columnWidth={viewMode === ViewMode.Month ? 400 : viewMode === ViewMode.Week ? 250 : 80}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectTimeline;
